import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/lib/seo";

const TYPES = ["White Paper", "Technical Note", "Research Note", "Architecture Paper",
  "Governance Guide", "Implementation Guide", "Position Paper", "Compliance Guide", "Case Study", "Best Practices"];
const empty = {
  title: "", pub_type: "White Paper", abstract: "", content: "", cover_image: "",
  authors_str: "AstroLife Research", keywords: "", version: "1.0", doi: "", pdf_url: "",
  language: "en", status: "draft", featured: false, seo_description: "",
};
const inputCls = "w-full rounded-lg border border-navy-border bg-navy px-3 py-2.5 text-sm text-white focus:border-astro-blue focus:outline-none";

export default function PublicationsAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: pubs, isLoading } = useQuery({
    queryKey: ["admin-pubs"],
    queryFn: async () => (await api.get("/admin/publications")).data,
  });

  const openNew = () => setEditing({ id: null, form: { ...empty } });
  const openEdit = async (id) => {
    const p = (await api.get(`/admin/publications/${id}`)).data;
    setEditing({ id, form: { ...p, authors_str: (p.authors || []).map((a) => a.name).join(", "), keywords: (p.keywords || []).join(", ") } });
  };
  const setF = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const save = useMutation({
    mutationFn: async ({ id, form }) => {
      const payload = {
        ...form,
        authors: form.authors_str.split(",").map((n) => n.trim()).filter(Boolean).map((name) => ({ name, role: "AstroLife" })),
        keywords: form.keywords.split(",").map((t) => t.trim()).filter(Boolean),
      };
      delete payload.authors_str;
      if (id) return (await api.put(`/admin/publications/${id}`, payload)).data;
      return (await api.post("/admin/publications", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-pubs"] }); setEditing(null); toast.success("Publication saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/publications/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-pubs"] }); toast.success("Publication deleted"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <>
      <Seo title="Publications CMS" description="" path="/admin/publications" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-black text-white">Publications</h1><p className="mt-2 text-silver/60">{(pubs || []).length} publications</p></div>
        <button onClick={openNew} data-testid="new-pub-btn" className="inline-flex items-center gap-2 rounded-full bg-astro-blue px-5 py-3 text-sm font-semibold text-white hover:bg-astro-light transition-colors"><Plus className="h-4 w-4" /> New publication</button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-navy-border bg-navy-surface">
        {isLoading ? <div className="p-10 text-center text-silver/50">Loading…</div> : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-navy-border text-xs uppercase tracking-wider text-silver/50">
              <tr><th className="px-5 py-4">Title</th><th className="hidden px-5 py-4 md:table-cell">Type</th><th className="px-5 py-4">Status</th><th className="px-5 py-4 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {(pubs || []).map((p) => (
                <tr key={p.id} className="border-b border-navy-border/50 hover:bg-navy" data-testid={`pub-row-${p.id}`}>
                  <td className="px-5 py-4"><p className="font-medium text-white">{p.title}</p></td>
                  <td className="hidden px-5 py-4 text-silver/70 md:table-cell">{p.pub_type}</td>
                  <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>{p.status}</span></td>
                  <td className="px-5 py-4"><div className="flex justify-end gap-2">
                    {p.status === "published" && <a href={`/publications/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-lg p-2 text-silver/60 hover:text-white"><ExternalLink className="h-4 w-4" /></a>}
                    <button onClick={() => openEdit(p.id)} data-testid={`edit-pub-${p.id}`} className="rounded-lg p-2 text-silver/60 hover:text-white"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => { if (window.confirm("Delete this publication?")) del.mutate(p.id); }} data-testid={`delete-pub-${p.id}`} className="rounded-lg p-2 text-silver/60 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="pub-editor">
          <div className="absolute inset-0 bg-black/60" onClick={() => setEditing(null)} />
          <div className="relative h-full w-full max-w-2xl overflow-y-auto border-l border-navy-border bg-navy-surface p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-white">{editing.id ? "Edit publication" : "New publication"}</h2>
              <button onClick={() => setEditing(null)} className="text-silver/60 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }} className="mt-6 space-y-4">
              <Field label="Title"><input required value={editing.form.title} onChange={(e) => setF("title", e.target.value)} data-testid="pub-title" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type"><select value={editing.form.pub_type} onChange={(e) => setF("pub_type", e.target.value)} data-testid="pub-type" className={inputCls}>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
                <Field label="Status"><select value={editing.form.status} onChange={(e) => setF("status", e.target.value)} data-testid="pub-status" className={inputCls}><option value="draft">Draft</option><option value="published">Published</option></select></Field>
              </div>
              <Field label="Abstract"><textarea rows={3} value={editing.form.abstract} onChange={(e) => setF("abstract", e.target.value)} data-testid="pub-abstract" className={inputCls} /></Field>
              <Field label="Content (HTML)"><textarea rows={10} value={editing.form.content} onChange={(e) => setF("content", e.target.value)} data-testid="pub-content" className={`${inputCls} font-mono text-xs`} /></Field>
              <Field label="Cover image URL"><input value={editing.form.cover_image} onChange={(e) => setF("cover_image", e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Version"><input value={editing.form.version} onChange={(e) => setF("version", e.target.value)} className={inputCls} /></Field>
                <Field label="DOI"><input value={editing.form.doi} onChange={(e) => setF("doi", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="PDF URL"><input value={editing.form.pdf_url} onChange={(e) => setF("pdf_url", e.target.value)} className={inputCls} /></Field>
              <Field label="Authors (comma-separated)"><input value={editing.form.authors_str} onChange={(e) => setF("authors_str", e.target.value)} className={inputCls} /></Field>
              <Field label="Keywords (comma-separated)"><input value={editing.form.keywords} onChange={(e) => setF("keywords", e.target.value)} className={inputCls} /></Field>
              <label className="flex items-center gap-2 text-sm text-silver/80"><input type="checkbox" checked={editing.form.featured} onChange={(e) => setF("featured", e.target.checked)} className="h-4 w-4 accent-astro-blue" /> Featured</label>
              <button type="submit" disabled={save.isPending} data-testid="save-pub" className="w-full rounded-full bg-astro-blue px-6 py-3.5 font-semibold text-white hover:bg-astro-light transition-colors disabled:opacity-60">{save.isPending ? "Saving…" : "Save publication"}</button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-sm font-medium text-silver/80">{label}</span>{children}</label>;
}
